/**
 * Shared Component Parsing Logic
 *
 * Extracted from parser.ts to be reused by page, overlay, dialog, and menu parsers.
 */

import { v4 as uuidv4 } from 'uuid'

import { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder.js'

import { isSpecialComponent, parseAiCompSettings, SPECIAL_COMPONENT_PARSERS } from './registry'
import type { AiComponentInput } from './registry'

/**
 * Component Group to Component IDs mapping
 * Extracted from DEFAULT_COMPONENTS_GROUPS to avoid shared folder dependency
 */
const COMPONENT_GROUP_MAP: Record<string, string[]> = {
  container: ['section', 'tabs', 'form'],
  text: ['text', 'rich-text'],
  button: ['button', 'fab', 'icon'],
  media: ['image', 'video', 'audio', 'array-gallery'],
  list: ['list', 'card', 'gallery', 'accordion', 'multi-location', 'shopping-cart', 'timeline'],
  table: ['data-table', 'data-list'],
  form: [
    'text-field',
    'multi-selection',
    'checkbox',
    'switch',
    'field-rating',
    'selection',
    'slider',
    'datetime-picker',
    'file',
    'image-upload',
    'icon-picker',
    'hidden-field',
    'phone-input',
  ],
  visual: ['map', 'divider'],
  progress: ['linear-progress', 'circular-progress'],
  chart: ['pie', 'bar'],
  app: ['login-form', 'vertical-menu', 'calendar', 'prompt', 'quantity', 'pricing', 'newsletter', 'alert'],
  interactive: ['comment', 'rating'],
  navigation: ['menu'],
}

/**
 * Helper function to find group ID for a component type
 */
function getGroupIdForComponent(componentType: string): string | undefined {
  for (const [groupId, components] of Object.entries(COMPONENT_GROUP_MAP)) {
    if (components.includes(componentType)) {
      return groupId
    }
  }
  return undefined
}

/**
 * Create component body structure
 */
function createComponentBody(componentId: string, input: AiComponentInput, children: IBody[] = []): IBody {
  // Find group id from COMPONENT_GROUP_MAP
  const groupId = getGroupIdForComponent(input.type)

  if (!groupId) {
    throw new Error(`Component type ${input.type} not found in COMPONENT_GROUP_MAP`)
  }

  return {
    _id: componentId,
    _ComponentID: input.type,
    _GroupID: groupId,
    _Title: input.title || '',
    _Body: children,
    _Draggable: true,
    _Removable: true,
    _IsSubmit: false,
    _Actions: {},
    _Behavior: {
      _Metadata: {},
      _Visibility: {
        _Type: '',
        _Clauses: [],
      },
      _UserControlledFilter: {
        _Type: '',
        _FilterBy: '',
        _ShowOnPage: false,
        _ShowOnFilterPage: false,
        _TitleIcon: { value: '' },
        _AllowMultiple: false,
      },
    },
  }
}

/**
 * Parse component recursively, handling children
 */
function parseComponentRecursive(input: AiComponentInput, bodySettings: IBodySettings): IBody {
  // Check if this is a special component that needs custom body structure
  if (isSpecialComponent(input.type)) {
    const specialParser = SPECIAL_COMPONENT_PARSERS[input.type]
    return specialParser(input, bodySettings, (childInput, settings) => parseComponentRecursive(childInput, settings))
  }

  // Normal component handling
  // Generate a single UUID for both body and settings
  const componentId = uuidv4()

  // Parse this component's settings
  const settings = parseAiCompSettings(input)

  // Add settings to flat bodySettings object
  bodySettings[componentId] = settings

  // Parse children recursively
  const childrenBodies: IBody[] = []
  if (input.children && input.children.length > 0) {
    for (const childInput of input.children) {
      const childBody = parseComponentRecursive(childInput, bodySettings)
      childrenBodies.push(childBody)
    }
  }

  // Create component body with children using the same componentId
  const body = createComponentBody(componentId, input, childrenBodies)

  return body
}

/**
 * Parse AI components to full body structure
 */
export function parseAiComponents(components: AiComponentInput[]) {
  // Create _Body structure (nested) and _BodySettings (flat)
  let bodies: IBody[] = []
  let bodySettings: IBodySettings = {}

  // Parse each top-level component recursively
  components.forEach((component) => {
    const body = parseComponentRecursive(component, bodySettings)
    bodies.push(body)
  })

  return {
    bodies,
    bodySettings,
  }
}
